import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useToast } from '../contexts/ToastContext';
import {
  ConfirmDialog,
  EmptyState,
  InlineMessage,
  NumberField,
  PageContainer,
  PageHeader,
  StatusChip,
  TabShell,
  UsageBar,
  parseNumberInput,
  statusLabel,
} from '../components/ui';
import { useUrlTab } from '../hooks/useUrlTab';
import { tokens } from '../theme/tokens';
import { formatWebsite } from '../utils/website';
import { GRANTABLE_FEATURES } from '../types/billing';
import {
  FeatureTierOption,
  discountPriceProblem,
  formatIntervalPrice,
  joinWords,
  tierFeaturePick,
  tierLimitsLeftOut,
} from './adminStores/grantPicks';
import AdminBrandAuthorizationsPanel from '../components/brandAuthorizations/AdminBrandAuthorizationsPanel';
import { bvRoutes } from './brandsVendors/brandVendorsRoutes';
import { REVIEWABLE_STORE_STATUSES, storeCaseId } from '../services/storeApprovalApi';
import { SuspendStoreDialog, useStoreStatusMutations } from './adminStores/StoreStatusActions';
import { GrantBadge } from './AdminStoreManagementPage';
import {
  SUBSCRIPTION_LABELS,
  StoreAccountPage,
  StoreAccountRow,
  daysLeft,
  formatPlanPrice,
  grantKindLabel,
  longDate,
  planLabel,
  planTone,
  relativeTime,
  storeStatusTone,
  subscriptionNote,
  subscriptionTone,
} from './adminStores/storeAccounts';

const TABS = ['overview', 'users', 'billing', 'integrations'] as const;
type TabKey = (typeof TABS)[number];

interface StoreUsage {
  planName: string;
  tier: string | null;
  hasAccessGrant: boolean;
  limits: { maxUsers: number; maxApiCalls: number; maxDataSync: number; maxIntegrations: number };
  usage: { apiCalls: number; dataSyncs: number; integrations: number; users: number };
  period: { start?: string; end?: string } | null;
}

interface StoreUser {
  userId: string;
  email?: string;
  displayName?: string;
  role?: string;
  lastActiveAt?: string | { _seconds?: number; seconds?: number };
}

interface StoreInvitation {
  id: string;
  email: string;
  role?: string;
}

interface IntegrationRow {
  integrationId: string;
  organizationId: string | null;
  platform: string;
  name: string;
  isActive: boolean;
  schedule: { label: string };
  shopLabel: string | null;
  lastSyncAt: string | null;
  lastSyncSummary: { errorCount?: number; warningCount?: number; status?: string } | null;
}

const ROLE_OPTIONS = [
  { value: 'employee', label: 'Staff' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
];

const roleLabel = (role?: string) =>
  role === 'employee' ? 'Staff' : role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Staff';

const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const asIso = (value: StoreUser['lastActiveAt']): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  const seconds = value._seconds ?? value.seconds;
  return typeof seconds === 'number' ? new Date(seconds * 1000).toISOString() : null;
};

const apiError = async (res: Response, fallback: string) => {
  const err = await res.json().catch(() => ({}));
  return new Error(err.error?.message || err.message || fallback);
};

const Card: React.FC<{ title?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; sx?: object }> = ({
  title,
  actions,
  children,
  sx,
}) => (
  <Paper sx={{ p: 2.5, minWidth: 0, ...sx }}>
    {(title || actions) && (
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }} spacing={1}>
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        {actions}
      </Stack>
    )}
    {children}
  </Paper>
);

const KeyValue: React.FC<{ label: string; children: React.ReactNode; mono?: boolean }> = ({ label, children, mono }) => (
  <Stack direction="row" spacing={2} sx={{ py: 0.75 }}>
    <Typography variant="body2" color="text.secondary" sx={{ width: 120, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ minWidth: 0, wordBreak: 'break-word', fontFamily: mono ? tokens.font.mono : undefined }}
    >
      {children}
    </Typography>
  </Stack>
);

const AttentionDot: React.FC<{ tone: 'caution' | 'problem' }> = ({ tone }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: 'inline-block',
      width: 6,
      height: 6,
      borderRadius: '50%',
      ml: 0.75,
      verticalAlign: 'middle',
      bgcolor: tokens.signal[tone].solid,
    }}
  />
);

const AdminStoreDetailPage: React.FC = () => {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { startImpersonationWithOrg } = useImpersonation();
  const { reactivate } = useStoreStatusMutations();
  const [tab, setTab] = useUrlTab(TABS, 'overview');
  const [suspendFor, setSuspendFor] = useState<{ id: string; name: string } | null>(null);

  // --- data ---------------------------------------------------------------
  const { data: orgData, isLoading: orgLoading, isError: orgError } = useQuery({
    queryKey: ['admin-store-details', orgId],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/admin/organizations/${orgId}`, { headers });
      if (!res.ok) throw await apiError(res, 'Failed to load store details');
      const body = await res.json();
      return (body.data || body.organization || body) as any;
    },
    enabled: !!orgId,
  });

  // The list route already resolves plan, grant, subscription and seats for a
  // store; searching by id gives this page the same answer the list showed.
  const { data: account } = useQuery({
    queryKey: ['admin-store-accounts', 'one', orgId],
    queryFn: async (): Promise<StoreAccountRow | null> => {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ search: orgId, limit: '5', sort: 'name' });
      const res = await makeApiCall(`/admin/organizations/store-accounts?${params}`, { headers });
      if (!res.ok) return null;
      const body = await res.json();
      return ((body.data as StoreAccountPage).rows || []).find((r) => r.id === orgId) ?? null;
    },
    enabled: !!orgId,
  });

  const { data: usage } = useQuery({
    queryKey: ['admin-org-usage', orgId],
    queryFn: async (): Promise<StoreUsage> => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/billing/admin/organizations/${orgId}/usage`, { headers });
      if (!res.ok) throw await apiError(res, 'Failed to load usage');
      return (await res.json()).data as StoreUsage;
    },
    enabled: !!orgId,
  });

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['admin-store-users', orgId],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/admin/organizations/${orgId}/users?limit=100`, { headers });
      if (!res.ok) throw await apiError(res, 'Failed to load store users');
      const body = await res.json();
      return {
        users: (body.data?.users || []) as StoreUser[],
        invitations: (body.data?.invitations || []) as StoreInvitation[],
      };
    },
    enabled: !!orgId,
  });

  const { data: grantData, isFetched: grantFetched } = useQuery({
    queryKey: ['admin-access-grant', orgId],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/billing/admin/organizations/${orgId}/access-grant`, { headers });
      if (!res.ok) throw await apiError(res, 'Failed to load access grant');
      return res.json();
    },
    enabled: !!orgId,
  });

  const { data: integrations = [] } = useQuery({
    queryKey: ['admin-store-integrations', orgId],
    queryFn: async (): Promise<IntegrationRow[]> => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/admin/integrations/store-sync-overview', { headers });
      if (!res.ok) throw await apiError(res, 'Failed to load integrations');
      const body = await res.json();
      return ((body.integrations || []) as IntegrationRow[]).filter((i) => i.organizationId === orgId);
    },
    enabled: !!orgId,
  });

  const { data: featureTiersData } = useQuery({
    queryKey: ['admin-feature-tiers'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/billing/admin/feature-tiers', { headers });
      if (!res.ok) throw await apiError(res, 'Failed to load feature tiers');
      return res.json();
    },
    enabled: tab === 'billing',
  });

  const { data: waiverData } = useQuery({
    queryKey: ['admin-setup-fee-waiver', orgId],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/billing/admin/organizations/${orgId}/setup-fee-waiver`, { headers });
      if (!res.ok) throw await apiError(res, 'Failed to load setup fee waiver');
      return res.json();
    },
    enabled: !!orgId && tab === 'billing',
  });

  const { data: waiverCampaignsData } = useQuery({
    queryKey: ['admin-setup-fee-campaigns'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/billing/admin/setup-fee-campaigns', { headers });
      if (!res.ok) throw await apiError(res, 'Failed to load waiver campaigns');
      return res.json();
    },
    enabled: tab === 'billing',
  });

  const org = orgData;
  const name: string = org?.displayName || org?.name || account?.name || 'Store';
  const status: string = org?.status || account?.status || 'active';
  const users = usersData?.users ?? [];
  const invitations = usersData?.invitations ?? [];
  const featureTiers: Array<{ tier: string; label: string }> = (featureTiersData?.data || [
    { tier: 'starter', label: 'Starter' },
    { tier: 'pro', label: 'Pro' },
    { tier: 'intelligence', label: 'Intelligence' },
    { tier: 'complete', label: 'Complete' },
    { tier: 'full', label: 'Full Access (all features)' },
  ]).filter((t: { tier: string }) => t.tier !== 'free');
  // Every tier with its price and limits, free included (a store's own plan may be Free Preview).
  const tierOptions: FeatureTierOption[] = featureTiersData?.data || [];
  const tierOption = (tier: string | null | undefined) => tierOptions.find((t) => t.tier === tier);

  const seats = account?.seats;
  const seatsUsed = seats?.used ?? users.length;
  const seatLimit = seats?.limit ?? usage?.limits.maxUsers ?? -1;
  const overSeatLimit = seatLimit > 0 && seatsUsed > seatLimit;
  const atSeatLimit = seatLimit > 0 && seatsUsed >= seatLimit;
  const failingIntegrations = integrations.filter((i) => (i.lastSyncSummary?.errorCount ?? 0) > 0).length;
  const subState = account?.subscription.state;
  const pendingReview = REVIEWABLE_STORE_STATUSES.includes(status);

  // --- mutations -----------------------------------------------------------
  const refreshAccess = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-store-accounts'] });
    queryClient.invalidateQueries({ queryKey: ['admin-access-grant'] });
    queryClient.invalidateQueries({ queryKey: ['admin-org-usage'] });
    queryClient.invalidateQueries({ queryKey: ['admin-store-details'] });
  };

  const [invite, setInvite] = useState({ email: '', role: 'employee' });
  const inviteMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/admin/organizations/${orgId}/invite`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email: invite.email.trim(), role: invite.role }),
      });
      if (!res.ok) throw await apiError(res, 'Failed to send the invite');
      return res.json();
    },
    onSuccess: () => {
      showToast(`Invite sent to ${invite.email.trim()}`, 'success');
      setInvite({ email: '', role: 'employee' });
      queryClient.invalidateQueries({ queryKey: ['admin-store-users', orgId] });
    },
    onError: (e: Error) => showToast(e.message, 'error'),
  });

  const [removeUser, setRemoveUser] = useState<StoreUser | null>(null);
  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/admin/organizations/${orgId}/users/${userId}`, { method: 'DELETE', headers });
      if (!res.ok) throw await apiError(res, 'Failed to remove the user');
      return res.json();
    },
    onSuccess: () => {
      showToast('User removed', 'success');
      setRemoveUser(null);
      queryClient.invalidateQueries({ queryKey: ['admin-store-users', orgId] });
      queryClient.invalidateQueries({ queryKey: ['admin-store-accounts'] });
    },
    onError: (e: Error) => showToast(e.message, 'error'),
  });

  // Grant form: the string in the Duration box is the only state; it is parsed on submit.
  const [grantTier, setGrantTier] = useState('pro');
  const [grantKind, setGrantKind] = useState<'beta' | 'trial' | 'comp'>('trial');
  const [grantDaysText, setGrantDaysText] = useState('30');
  const [grantBillingMode, setGrantBillingMode] = useState<'local' | 'stripe_comp' | 'stripe_discount'>('local');
  const [discountText, setDiscountText] = useState('');
  const [grandfatherPick, setGrandfatherPick] = useState<{ label: string; added: number; leftOut: string[] } | null>(
    null
  );
  const [grandfatherKeys, setGrandfatherKeys] = useState<string[]>([]);
  const grantDaysParsed = parseNumberInput(grantDaysText);
  const grantDaysValid =
    grantDaysParsed !== null && Number.isInteger(grantDaysParsed) && grantDaysParsed >= 1 && grantDaysParsed <= 3650;
  const discountParsed = parseNumberInput(discountText);
  const storedGrant = grantData?.data?.accessGrant;

  // Keep the form in step with the stored grant (otherwise it sits on Pro/Trial after reload).
  useEffect(() => {
    if (!grantFetched) return;
    if (!storedGrant) {
      setGrantTier('pro');
      setGrantKind('trial');
      setGrantDaysText('30');
      setGrantBillingMode('local');
      setDiscountText('');
      setGrandfatherKeys([]);
      setGrandfatherPick(null);
      return;
    }
    if (typeof storedGrant.tier === 'string' && storedGrant.tier) setGrantTier(storedGrant.tier);
    if (['beta', 'trial', 'comp'].includes(storedGrant.kind)) setGrantKind(storedGrant.kind);
    if (['local', 'stripe_comp', 'stripe_discount'].includes(storedGrant.billingMode)) {
      setGrantBillingMode(storedGrant.billingMode);
    }
    setDiscountText(
      typeof storedGrant.stripeDiscount?.price === 'number' ? String(storedGrant.stripeDiscount.price) : ''
    );
    if (storedGrant.expiresAt) {
      const ms = new Date(storedGrant.expiresAt).getTime() - Date.now();
      setGrantDaysText(String(Math.min(3650, Math.max(1, Math.ceil(ms / 86_400_000)))));
    } else {
      setGrantDaysText('30');
    }
    // The backend replaces the list with what is posted, so hydrate it: an
    // empty select would otherwise drop capabilities already granted.
    setGrandfatherKeys(Array.isArray(storedGrant.grantedFeatures) ? storedGrant.grantedFeatures : []);
  }, [grantFetched, storedGrant]);

  const grantMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/billing/admin/organizations/${orgId}/access-grant`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          kind: grantKind,
          tier: grantTier,
          durationDays: grantKind === 'trial' ? (grantDaysParsed ?? undefined) : undefined,
          billingMode: grantBillingMode,
          ...(grantBillingMode === 'stripe_discount' ? { discountedPrice: discountParsed ?? undefined } : {}),
          reason: `Admin ${grantKind} grant at ${grantTier} tier`,
        }),
      });
      if (!res.ok) throw await apiError(res, 'Failed to grant access');
      return res.json();
    },
    onSuccess: () => {
      refreshAccess();
      showToast(`${grantKindLabel(grantKind)} grant applied`, 'success');
    },
    onError: (e: Error) => showToast(e.message, 'error'),
  });

  const revokeMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/billing/admin/organizations/${orgId}/access-grant`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) throw await apiError(res, 'Failed to revoke access');
      return res.json();
    },
    onSuccess: () => {
      refreshAccess();
      showToast(`Grant revoked. ${name} is back on ${planLabel(account?.planTier)}.`, 'success');
    },
    onError: (e: Error) => showToast(e.message, 'error'),
  });

  /**
   * Grandfather grant: adds boolean features on top of the store's own plan.
   * A separate call from the tier grant on purpose: a tier grant REPLACES the
   * limits, this one only adds, and mixing them is how a paying store gets
   * silently moved to a plan it did not buy.
   */
  const grandfatherMutation = useMutation({
    mutationFn: async (features: string[]) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/billing/admin/organizations/${orgId}/access-grant`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          kind: 'comp',
          grantedFeatures: features,
          reason: `Grandfathered: ${features.join(', ')}`,
        }),
      });
      if (!res.ok) throw await apiError(res, 'Failed to grant features');
      return res.json();
    },
    onSuccess: () => {
      refreshAccess();
      setGrandfatherPick(null);
      showToast('Grandfathered features saved', 'success');
    },
    onError: (e: Error) => showToast(e.message, 'error'),
  });

  const grandfatherSelectionChanged = useMemo(() => {
    const stored: string[] = Array.isArray(storedGrant?.grantedFeatures) ? storedGrant.grantedFeatures : [];
    const same = stored.length === grandfatherKeys.length && stored.every((k) => grandfatherKeys.includes(k));
    if (same) return false;
    if (grandfatherKeys.length > 0) return true;
    // A features-only grant with no features is rejected by the API; Revoke grant clears that one.
    return Boolean(storedGrant?.tier);
  }, [storedGrant, grandfatherKeys]);

  // Stripe discount (MOT-681): moves the store's own Stripe subscription, so it needs one,
  // and never one a comp grant created (the server refuses that too).
  const hasStripeSubscription = Boolean(account?.subscription.stripeSubscriptionId);
  const onCompSubscription = storedGrant?.billingMode === 'stripe_comp';
  const grantTierOption = tierOption(grantTier);
  const discountProblem =
    grantBillingMode === 'stripe_discount' ? discountPriceProblem(grantTierOption, discountParsed) : null;

  // Quick picks on Grandfather extra features: the on/off features a tier has and their plan lacks.
  const ownLimits = tierOption(account?.planTier)?.limits;
  const pickTiers = tierOptions.filter((t) => ['pro', 'intelligence', 'complete'].includes(t.tier) && t.limits);
  const applyTierPick = (option: FeatureTierOption) => {
    const grantable = GRANTABLE_FEATURES.map((f) => f.key as string);
    const picked = tierFeaturePick(option.limits, ownLimits, grantable);
    const added = picked.filter((key) => !grandfatherKeys.includes(key));
    setGrandfatherKeys(grantable.filter((key) => grandfatherKeys.includes(key) || picked.includes(key)));
    setGrandfatherPick({
      label: option.label,
      added: added.length,
      leftOut: tierLimitsLeftOut(option.limits, ownLimits),
    });
  };

  const [waiverCampaign, setWaiverCampaign] = useState('');
  const [waiverReason, setWaiverReason] = useState('');
  const waiverCampaigns: Array<{ key: string; label: string; expiresAt: string; open: boolean; promotionCode?: string | null }> =
    waiverCampaignsData?.data || [];

  const waiverMutation = useMutation({
    mutationFn: async (remove: boolean) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/billing/admin/organizations/${orgId}/setup-fee-waiver`, {
        method: remove ? 'DELETE' : 'POST',
        headers,
        ...(remove
          ? {}
          : {
              body: JSON.stringify(
                waiverCampaign
                  ? { campaign: waiverCampaign, reason: waiverReason.trim() || undefined }
                  : { reason: waiverReason.trim() }
              ),
            }),
      });
      if (!res.ok) throw await apiError(res, 'Failed to update the waiver');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-setup-fee-waiver', orgId] });
      showToast('Setup fee waiver updated', 'success');
    },
    onError: (e: Error) => showToast(e.message, 'error'),
  });

  // --- actions ------------------------------------------------------------
  const impersonate = () => {
    startImpersonationWithOrg({
      id: orgId,
      name,
      type: 'store',
      email: org?.contactInfo?.primaryContact?.email,
      phone: org?.contactInfo?.primaryContact?.phone,
      address: org?.address
        ? `${org.address.street || ''}, ${org.address.city || ''}, ${org.address.state || ''}`.trim()
        : '',
      status: status === 'active' ? 'active' : status.startsWith('pending') ? 'pending' : 'inactive',
      plan: planLabel(account?.planTier),
    });
    navigate('/app/dashboard');
  };

  const openErpSync = () => navigate(`/app/admin/store-sync-health?org=${encodeURIComponent(orgId)}`);
  const openApprovals = () => navigate(bvRoutes.approvals(storeCaseId(orgId)));
  const stripeUrl = account?.subscription.stripeSubscriptionId
    ? `https://dashboard.stripe.com/subscriptions/${account.subscription.stripeSubscriptionId}`
    : null;

  const website = formatWebsite(org?.website);
  const contactEmail = org?.contactInfo?.primaryContact?.email || org?.email;
  const contactPhone = org?.contactInfo?.primaryContact?.phone || org?.phone;
  const address = org?.address
    ? [org.address.city, org.address.state].filter(Boolean).join(', ')
    : '';

  // --- render -------------------------------------------------------------
  if (orgError) {
    return (
      <PageContainer>
        <PageHeader title="Store not found" backTo="/app/admin/stores" backLabel="Stores" />
        <InlineMessage severity="error">This store could not be loaded. It may have been removed.</InlineMessage>
      </PageContainer>
    );
  }

  const grantTone = storedGrant?.kind === 'trial' ? 'caution' : 'info';
  const grantDays = daysLeft(storedGrant?.expiresAt);

  const statCard = (label: string, value: React.ReactNode, note: React.ReactNode, target: TabKey, noteColor?: string) => (
    <Paper
      key={label}
      onClick={() => setTab(target)}
      sx={{ p: 2, cursor: 'pointer', minWidth: 0, '&:hover': { borderColor: tokens.accent.border } }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: noteColor || 'text.secondary' }}>
        {note || ' '}
      </Typography>
    </Paper>
  );

  const periodLabel =
    usage?.period?.start && usage?.period?.end
      ? `${longDate(usage.period.start)} – ${longDate(usage.period.end)}`
      : 'Current billing period';

  const banner = (() => {
    if (pendingReview)
      return {
        severity: 'warning' as const,
        text: 'Pending approval. Approve or reject this store in the Approvals queue before it can use Motzu.',
        action: <Button color="inherit" size="small" onClick={openApprovals}>Review in Approvals queue</Button>,
      };
    if (subState === 'past_due')
      return {
        severity: 'error' as const,
        text: 'Payment failed. Stripe retries failed payments automatically; check the subscription in Stripe for the retry schedule.',
        action: stripeUrl ? (
          <Button color="inherit" size="small" href={stripeUrl} target="_blank" rel="noopener noreferrer">
            Open in Stripe
          </Button>
        ) : undefined,
      };
    if (status === 'suspended')
      return {
        severity: 'error' as const,
        text: 'Suspended. This store is marked suspended and hidden from brand store lists. Members can still sign in and syncs still run.',
        action: (
          <Button
            color="inherit"
            size="small"
            onClick={() => reactivate.mutate({ orgId, name })}
            disabled={reactivate.isPending}
          >
            Reactivate store
          </Button>
        ),
      };
    return null;
  })();

  return (
    <PageContainer>
      <PageHeader
        title={
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <span>{name}</span>
            <StatusChip label={statusLabel(status)} tone={storeStatusTone(status)} />
            {account && <StatusChip label={planLabel(account.planTier)} tone={planTone(account.planTier)} />}
            {account?.accessGrant && <GrantBadge grant={account.accessGrant} />}
          </Stack>
        }
        description={
          <>
            {website ? (
              <Link href={website.href} target="_blank" rel="noopener noreferrer" underline="hover">
                {website.label}
              </Link>
            ) : (
              'No website'
            )}
            {' · '}Created {longDate(account?.createdAt)}
            {' · '}
            <Box
              component="button"
              type="button"
              onClick={() => {
                navigator.clipboard?.writeText(orgId);
                showToast('Organization ID copied', 'success');
              }}
              sx={{
                font: 'inherit',
                fontFamily: tokens.font.mono,
                fontSize: 12,
                border: 0,
                bgcolor: 'transparent',
                p: 0,
                color: 'inherit',
                cursor: 'pointer',
                textDecoration: 'underline dotted',
              }}
              title="Copy organization ID"
            >
              {orgId}
            </Box>
          </>
        }
        backTo="/app/admin/stores"
        backLabel="Stores"
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={impersonate}>
              Impersonate
            </Button>
            <Button variant="outlined" onClick={openErpSync}>
              Open ERP sync
            </Button>
            {status === 'suspended' ? (
              <Button
                variant="outlined"
                color="success"
                onClick={() => reactivate.mutate({ orgId, name })}
                disabled={reactivate.isPending}
              >
                Reactivate
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="error"
                disabled={pendingReview}
                onClick={() => setSuspendFor({ id: orgId, name })}
              >
                Suspend…
              </Button>
            )}
          </Stack>
        }
      />

      {banner && (
        <Alert severity={banner.severity} action={banner.action} sx={{ mb: 2 }}>
          {banner.text}
        </Alert>
      )}

      <TabShell
        sticky={false}
        value={tab}
        onChange={setTab}
        aria-label="Store sections"
        tabs={[
          {
            key: 'overview',
            label: (
              <>
                Overview
                {banner && <AttentionDot tone={banner.severity === 'warning' ? 'caution' : 'problem'} />}
              </>
            ),
          },
          {
            key: 'users',
            label: (
              <>
                Users {users.length > 0 && <span>({users.length})</span>}
                {overSeatLimit && <AttentionDot tone="problem" />}
              </>
            ),
          },
          {
            key: 'billing',
            label: (
              <>
                Billing & access
                {subState === 'past_due' && <AttentionDot tone="problem" />}
              </>
            ),
          },
          {
            key: 'integrations',
            label: (
              <>
                Integrations {integrations.length > 0 && <span>({integrations.length})</span>}
                {failingIntegrations > 0 && <AttentionDot tone="problem" />}
              </>
            ),
          },
        ]}
      >
        {orgLoading && !org ? (
          <InlineMessage severity="info">Loading store…</InlineMessage>
        ) : tab === 'overview' ? (
          <Stack spacing={2}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(auto-fit, minmax(200px, 1fr))' }, gap: 2 }}>
              {statCard(
                'Plan',
                account?.accessGrant && account.accessGrant.tier && account.accessGrant.tier !== account.planTier
                  ? `${planLabel(account.planTier)} → ${planLabel(account.accessGrant.tier)}`
                  : planLabel(account?.planTier),
                account?.accessGrant
                  ? `${grantKindLabel(account.accessGrant.kind)} grant${
                      account.accessGrant.kind === 'trial' && daysLeft(account.accessGrant.expiresAt) !== null
                        ? `, ${daysLeft(account.accessGrant.expiresAt)} days left`
                        : ''
                    }`
                  : account?.planName || 'No subscription',
                'billing',
                account?.accessGrant?.kind === 'trial' ? tokens.signal.caution.ink : undefined
              )}
              {statCard(
                'Subscription',
                account ? SUBSCRIPTION_LABELS[account.subscription.state] : '—',
                account ? subscriptionNote(account.subscription) : '',
                'billing',
                account ? tokens.signal[subscriptionTone(account.subscription.state)].ink : undefined
              )}
              {statCard(
                'Seats',
                seatLimit < 0 ? `${seatsUsed}` : `${seatsUsed} of ${seatLimit}`,
                invitations.length ? `${invitations.length} invite${invitations.length === 1 ? '' : 's'} pending` : 'No pending invites',
                'users',
                overSeatLimit ? tokens.signal.problem.ink : undefined
              )}
              {statCard(
                'Last active',
                relativeTime(account?.lastActive?.at),
                account?.lastActive?.userName || '',
                'users'
              )}
            </Box>

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-start">
              <Card
                title="Usage vs plan limits"
                sx={{ flex: '2 1 420px' }}
                actions={
                  <Typography variant="caption" color="text.secondary">
                    {periodLabel}
                    {usage?.planName ? ` · ${usage.planName}` : ''}
                  </Typography>
                }
              >
                {usage ? (
                  <Stack spacing={2}>
                    <UsageBar
                      label="API calls"
                      used={usage.usage.apiCalls}
                      limit={usage.limits.maxApiCalls === -1 ? null : usage.limits.maxApiCalls}
                    />
                    <UsageBar
                      label="Data sync jobs"
                      used={usage.usage.dataSyncs}
                      limit={usage.limits.maxDataSync === -1 ? null : usage.limits.maxDataSync}
                    />
                    <UsageBar
                      label="Live integrations"
                      used={usage.usage.integrations}
                      limit={usage.limits.maxIntegrations === -1 ? null : usage.limits.maxIntegrations}
                    />
                    <UsageBar
                      label="Users"
                      used={seatsUsed}
                      limit={seatLimit === -1 ? null : seatLimit}
                    />
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No usage data available.
                  </Typography>
                )}
              </Card>
              <Card title="Contact & account" sx={{ flex: '1 1 300px' }}>
                <KeyValue label="Primary contact">
                  {org?.contactInfo?.primaryContact?.name || org?.primaryContactDisplayName || '—'}
                </KeyValue>
                <KeyValue label="Email">{contactEmail || '—'}</KeyValue>
                <KeyValue label="Phone">{contactPhone || '—'}</KeyValue>
                <KeyValue label="Website">{website ? website.label : '—'}</KeyValue>
                <KeyValue label="Location">{address || '—'}</KeyValue>
                <KeyValue label="Tax ID">{org?.taxId || '—'}</KeyValue>
                <KeyValue label="Organization ID" mono>
                  {orgId}
                </KeyValue>
              </Card>
            </Stack>

            <AdminBrandAuthorizationsPanel organizationId={orgId} />
          </Stack>
        ) : tab === 'users' ? (
          <Card
            title="Users"
            actions={
              seatLimit > 0 ? (
                <Typography
                  variant="caption"
                  sx={{ color: atSeatLimit ? tokens.signal.caution.ink : 'text.secondary' }}
                >
                  {seatsUsed} of {seatLimit} seats used on {planLabel(account?.accessGrant?.tier || account?.planTier)}
                  {atSeatLimit ? '. At the limit: inviting more needs a bigger plan or a grant.' : ''}
                </Typography>
              ) : undefined
            }
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
              <TextField
                size="small"
                type="email"
                label="Invite by email"
                value={invite.email}
                onChange={(e) => setInvite((p) => ({ ...p, email: e.target.value }))}
                sx={{ flex: 1, minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  value={invite.role}
                  onChange={(e) => setInvite((p) => ({ ...p, role: e.target.value }))}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                disabled={!isEmail(invite.email) || inviteMutation.isPending}
                onClick={() => inviteMutation.mutate()}
              >
                Invite user
              </Button>
            </Stack>
            {usersLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading users…
              </Typography>
            ) : users.length + invitations.length === 0 ? (
              <EmptyState size="compact" title="No users yet." description="Invite the store owner to get started." />
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: 740 }}>
                  {users.map((u) => (
                    <Stack
                      key={u.userId}
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ py: 1.25, borderTop: `1px solid ${tokens.line.hairline}` }}
                    >
                      <Box sx={{ flex: '1.4 1 200px', minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {u.displayName || u.email || 'Unknown user'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {u.email}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ width: 100 }}>
                        {roleLabel(u.role)}
                      </Typography>
                      <Box sx={{ width: 120 }}>
                        <StatusChip label="Active" tone="positive" />
                      </Box>
                      <Typography variant="body2" sx={{ width: 140 }} color="text.secondary">
                        {relativeTime(asIso(u.lastActiveAt))}
                      </Typography>
                      <Box sx={{ width: 100, textAlign: 'right' }}>
                        <Button
                          size="small"
                          color="error"
                          disabled={u.role === 'owner'}
                          onClick={() => setRemoveUser(u)}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Stack>
                  ))}
                  {invitations.map((inv) => (
                    <Stack
                      key={inv.id}
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ py: 1.25, borderTop: `1px solid ${tokens.line.hairline}` }}
                    >
                      <Box sx={{ flex: '1.4 1 200px', minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {inv.email}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ width: 100 }}>
                        {roleLabel(inv.role)}
                      </Typography>
                      <Box sx={{ width: 120 }}>
                        <StatusChip label="Invited" tone="info" />
                      </Box>
                      <Typography variant="body2" sx={{ width: 140 }} color="text.secondary">
                        Not yet
                      </Typography>
                      <Box sx={{ width: 100 }} />
                    </Stack>
                  ))}
                </Box>
              </Box>
            )}
          </Card>
        ) : tab === 'billing' ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-start">
              <Card title="Subscription" sx={{ flex: '1 1 320px' }}>
                <KeyValue label="Plan">{account?.planName || 'No subscription'}</KeyValue>
                <KeyValue label="Status">
                  {account ? SUBSCRIPTION_LABELS[account.subscription.state] : '—'}
                </KeyValue>
                <KeyValue label="Price">
                  {account ? formatPlanPrice(account.subscription) || '—' : '—'}
                </KeyValue>
                <KeyValue label="Next event">{account ? subscriptionNote(account.subscription).split(' · ').slice(-1)[0] || '—' : '—'}</KeyValue>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  {stripeUrl && (
                    <Button variant="outlined" size="small" href={stripeUrl} target="_blank" rel="noopener noreferrer">
                      Open in Stripe
                    </Button>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Plan changes and refunds happen in Stripe.
                  </Typography>
                </Stack>
              </Card>

              <Card
                title="Access grant"
                sx={{ flex: '1 1 380px' }}
                actions={
                  <Typography variant="caption" color="text.secondary">
                    Replaces the plan&apos;s limits while active
                  </Typography>
                }
              >
                {storedGrant ? (
                  <Box
                    sx={{
                      p: 1.5,
                      mb: 2,
                      borderRadius: `${tokens.radius.small}px`,
                      border: `1px dashed ${tokens.signal[grantTone].solid}`,
                      bgcolor: tokens.signal[grantTone].row,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ color: tokens.signal[grantTone].ink }}>
                          {grantKindLabel(storedGrant.kind)} grant{storedGrant.tier ? `: ${planLabel(storedGrant.tier)}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {storedGrant.expiresAt
                            ? `${grantDays ?? 0} days left`
                            : 'No expiry'}
                          {' · '}
                          {storedGrant.billingMode === 'stripe_comp'
                            ? 'Stripe 100% off coupon'
                            : storedGrant.billingMode === 'stripe_discount' && storedGrant.stripeDiscount
                              ? `Stripe discount: pays ${formatIntervalPrice(
                                  storedGrant.stripeDiscount.price,
                                  tierOption(storedGrant.tier)?.interval,
                                  storedGrant.stripeDiscount.currency
                                )}`
                              : 'Local only (no Stripe charge)'}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => revokeMutation.mutate()}
                        disabled={revokeMutation.isPending}
                      >
                        Revoke grant
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No grant. {name} is on the limits of its own plan.
                  </Typography>
                )}

                <Stack spacing={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Feature tier</InputLabel>
                    <Select label="Feature tier" value={grantTier} onChange={(e) => setGrantTier(e.target.value)}>
                      {featureTiers.map((t) => (
                        <MenuItem key={t.tier} value={t.tier}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel>Grant type</InputLabel>
                    <Select
                      label="Grant type"
                      value={grantKind}
                      onChange={(e) => setGrantKind(e.target.value as 'beta' | 'trial' | 'comp')}
                    >
                      <MenuItem value="trial">Trial (timed)</MenuItem>
                      <MenuItem value="beta">Beta (no expiry by default)</MenuItem>
                      <MenuItem value="comp">Complimentary</MenuItem>
                    </Select>
                  </FormControl>
                  {grantKind === 'trial' && (
                    <NumberField
                      label="Duration (days)"
                      integer
                      min={1}
                      max={3650}
                      adornment="days"
                      allowEmpty={false}
                      value={grantDaysText}
                      onChange={setGrantDaysText}
                    />
                  )}
                  <FormControl fullWidth size="small">
                    <InputLabel>Billing mode</InputLabel>
                    <Select
                      label="Billing mode"
                      value={grantBillingMode}
                      onChange={(e) =>
                        setGrantBillingMode(e.target.value as 'local' | 'stripe_comp' | 'stripe_discount')
                      }
                    >
                      <MenuItem value="local">Local only (no Stripe charge)</MenuItem>
                      <MenuItem value="stripe_comp">Stripe 100% off coupon</MenuItem>
                      <MenuItem value="stripe_discount" disabled={!hasStripeSubscription || onCompSubscription}>
                        Stripe discount (they pay a set price)
                        {!hasStripeSubscription
                          ? ': needs a Stripe subscription'
                          : onCompSubscription
                            ? ': revoke the comp grant first'
                            : ''}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  {grantBillingMode === 'stripe_discount' && (
                    <NumberField
                      label="Price they pay"
                      adornment="$"
                      min={0}
                      value={discountText}
                      onChange={setDiscountText}
                      error={Boolean(discountText) && Boolean(discountProblem)}
                      helperText={
                        discountProblem ||
                        (grantTierOption && typeof grantTierOption.price === 'number' && discountParsed !== null
                          ? `${grantTierOption.label} lists at ${formatIntervalPrice(
                              grantTierOption.price,
                              grantTierOption.interval,
                              grantTierOption.currency
                            )}; the coupon takes ${formatIntervalPrice(
                              Math.round((grantTierOption.price - discountParsed) * 100) / 100,
                              grantTierOption.interval,
                              grantTierOption.currency
                            )} off.`
                          : undefined)
                      }
                    />
                  )}
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Button
                      variant="contained"
                      disabled={
                        grantMutation.isPending ||
                        (grantKind === 'trial' && !grantDaysValid) ||
                        Boolean(discountProblem)
                      }
                      onClick={() => grantMutation.mutate()}
                    >
                      {storedGrant?.tier ? 'Replace grant' : `Grant ${grantTier} tier`}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Grants {planLabel(grantTier)} limits
                      {grantKind === 'trial' && grantDaysValid ? ` for ${grantDaysParsed} days` : ''}.{' '}
                      {grantBillingMode === 'stripe_discount' ? (
                        <>
                          Moves their Stripe subscription to {planLabel(grantTier)}, billed at the price above from the
                          next invoice with no prorated charge. Revoking
                          {grantKind === 'trial' ? ' or the trial ending' : ''} puts them back on{' '}
                          {planLabel(account?.planTier)} at its own price.
                        </>
                      ) : (
                        <>
                          {grantBillingMode === 'local' ? 'No Stripe charge.' : 'Stripe 100% off coupon.'} Their paid
                          plan stays {planLabel(account?.planTier)}.
                        </>
                      )}
                    </Typography>
                  </Stack>
                </Stack>
              </Card>
            </Stack>

            <Card title="Grandfather extra features">
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Adds these on top of the store&apos;s own plan. Unlike a tier grant it never changes their plan, never
                removes anything and never moves a numeric limit. Use it when a capability they already had becomes
                paid.
              </Typography>
              <Stack spacing={1.5} sx={{ maxWidth: 560 }}>
                {pickTiers.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {pickTiers.map((option) => (
                      <Button key={option.tier} size="small" variant="outlined" onClick={() => applyTierPick(option)}>
                        Add {option.label} features
                      </Button>
                    ))}
                  </Stack>
                )}
                {grandfatherPick && (
                  <Typography variant="caption" color="text.secondary">
                    {grandfatherPick.added > 0
                      ? `Added ${grandfatherPick.added} ${grandfatherPick.label} feature${
                          grandfatherPick.added === 1 ? '' : 's'
                        } their plan lacks. Not saved yet.`
                      : `They already have every on/off feature ${grandfatherPick.label} has.`}
                    {grandfatherPick.leftOut.length > 0
                      ? ` Grandfathering cannot raise ${joinWords(grandfatherPick.leftOut)}; for all of ${
                          grandfatherPick.label
                        }, grant the tier as Complimentary, Local only.`
                      : ''}
                  </Typography>
                )}
                <FormControl fullWidth size="small">
                  <InputLabel>Features</InputLabel>
                  <Select
                    multiple
                    label="Features"
                    value={grandfatherKeys}
                    onChange={(e) =>
                      setGrandfatherKeys(
                        typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[])
                      )
                    }
                    renderValue={(selected) =>
                      GRANTABLE_FEATURES.filter((f) => (selected as string[]).includes(f.key as string))
                        .map((f) => f.label)
                        .join(', ')
                    }
                  >
                    {GRANTABLE_FEATURES.map((f) => (
                      <MenuItem key={f.key as string} value={f.key as string}>
                        <Checkbox checked={grandfatherKeys.includes(f.key as string)} />
                        <ListItemText primary={f.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  The list is saved as shown: deselect a feature and save to take it back. Clearing the last one needs
                  a tier grant to hang on; otherwise use Revoke grant.
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    disabled={!grandfatherSelectionChanged || grandfatherMutation.isPending}
                    onClick={() => grandfatherMutation.mutate(grandfatherKeys)}
                  >
                    Save {grandfatherKeys.length || 'no'} grandfathered feature{grandfatherKeys.length === 1 ? '' : 's'}
                  </Button>
                </Box>
              </Stack>
            </Card>

            <Card title="Setup fee waiver">
              {waiverData?.data?.waiver && (
                <InlineMessage severity={waiverData.data.active ? 'info' : 'warning'}>
                  {waiverData.data.active ? 'Base setup fee waived' : 'Waiver expired'}
                  {waiverData.data.waiver.campaign ? ` (${waiverData.data.waiver.campaign})` : ''}
                  {waiverData.data.waiver.expiresAt
                    ? ` — ${waiverData.data.active ? 'through' : 'ended'} ${longDate(waiverData.data.waiver.expiresAt)}`
                    : ' — no expiry'}
                  {waiverData.data.waiver.reason ? `. ${waiverData.data.waiver.reason}` : ''}
                </InlineMessage>
              )}
              <Stack spacing={1.5} sx={{ maxWidth: 560 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Campaign</InputLabel>
                  <Select label="Campaign" value={waiverCampaign} onChange={(e) => setWaiverCampaign(e.target.value)}>
                    <MenuItem value="">One-off (reason required)</MenuItem>
                    {waiverCampaigns.map((c) => (
                      <MenuItem key={c.key} value={c.key} disabled={!c.open}>
                        {c.label} — signups through {longDate(c.expiresAt)}
                        {c.promotionCode ? ` · code ${c.promotionCode}` : ''}
                        {c.open ? '' : ' (closed)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label={waiverCampaign ? 'Note (optional)' : 'Reason'}
                  value={waiverReason}
                  onChange={(e) => setWaiverReason(e.target.value)}
                  inputProps={{ maxLength: 300 }}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    disabled={waiverMutation.isPending || (!waiverCampaign && !waiverReason.trim())}
                    onClick={() => waiverMutation.mutate(false)}
                  >
                    Waive setup fee
                  </Button>
                  <Button
                    color="warning"
                    disabled={waiverMutation.isPending || !waiverData?.data?.waiver}
                    onClick={() => waiverMutation.mutate(true)}
                  >
                    Remove waiver
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Skips the plan&apos;s one-time setup fee at Checkout. A campaign sets the expiry from its signup
                  window and records it as the store&apos;s acquisition source. Add-on and location fees are not
                  affected.
                </Typography>
              </Stack>
            </Card>
          </Stack>
        ) : (
          <Card
            title="Integrations"
            actions={
              <Button size="small" variant="outlined" onClick={openErpSync}>
                Open in Store ERP sync
              </Button>
            }
          >
            {integrations.length === 0 ? (
              <EmptyState
                size="compact"
                title="No integrations connected."
                description="The store connects Shopify, BigCommerce or Lightspeed from its settings."
              />
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: 620 }}>
                  {integrations.map((i) => {
                    const errors = i.lastSyncSummary?.errorCount ?? 0;
                    const warnings = i.lastSyncSummary?.warningCount ?? 0;
                    const health = !i.lastSyncAt
                      ? { label: 'Not synced yet', tone: 'neutral' as const }
                      : errors > 0
                        ? { label: 'Failing', tone: 'problem' as const }
                        : warnings > 0
                          ? { label: 'Warnings', tone: 'caution' as const }
                          : { label: 'Healthy', tone: 'positive' as const };
                    return (
                      <Stack
                        key={i.integrationId}
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        sx={{ py: 1.25, borderTop: `1px solid ${tokens.line.hairline}` }}
                      >
                        <Box sx={{ flex: '1.4 1 200px', minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                            {i.platform.replace(/_/g, ' ')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {i.shopLabel || i.name}
                          </Typography>
                        </Box>
                        <Box sx={{ width: 130 }}>
                          <StatusChip label={health.label} tone={health.tone} />
                        </Box>
                        <Typography variant="body2" sx={{ width: 160 }} color="text.secondary">
                          Last sync {relativeTime(i.lastSyncAt)}
                        </Typography>
                        <Typography variant="body2" sx={{ width: 120 }} color="text.secondary">
                          {i.schedule?.label}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Card>
        )}
      </TabShell>

      <ConfirmDialog
        open={!!removeUser}
        title={`Remove ${removeUser?.displayName || removeUser?.email || 'this user'}?`}
        message={`They lose access to ${name} immediately. Their account stays; they can be invited again.`}
        confirmLabel="Remove user"
        confirmColor="error"
        loading={removeMutation.isPending}
        onConfirm={() => removeUser && removeMutation.mutate(removeUser.userId)}
        onClose={() => setRemoveUser(null)}
      />
      <SuspendStoreDialog store={suspendFor} onClose={() => setSuspendFor(null)} />
    </PageContainer>
  );
};

export default AdminStoreDetailPage;
